/*
 * What a paste becomes (F-2 §10.1). Files go to the tray first; then the text is inserted the way
 * its shape asks for: literally when Shift was held, as formatting when it is markdown or HTML from
 * another application, as a code block when a code editor or a terminal copied it. The decision
 * itself is `classifyClipboard`, pure and tested against captured clipboards; this file is the glue
 * between it, the tray and the view, and every paste is one transaction — one undo step.
 */

import type { EditorView } from "prosemirror-view"
import { namePastedImage, pastedContent } from "../composer/attachments/staging.ts"
import { classifyClipboard } from "./clipboardKind.ts"
import type { Dialect } from "./dialect/index.ts"
import { documentFromHtml } from "./html/parseHtml.ts"
import type { EditorKit } from "./kit.ts"

const URL = /^https?:\/\/\S+$/i

export interface PasteDeps {
	attach: (files: File[]) => void
	/** True when the paste should be inserted as the characters it is: Shift was held. */
	literal: () => boolean
}

/**
 * One paste into a view over `kit`. The kit's dialect is a whole one — markdown and HTML pasted
 * into a message are read by the message's own parser and DOM rules — so this paste is the
 * composer's; a document's paste takes other routes over the same classifier.
 */
export function handlePaste(
	kit: EditorKit<Dialect>,
	view: EditorView,
	event: ClipboardEvent,
	deps: PasteDeps,
): boolean {
	const { M, N, dialect, sliceOfDocument, sliceOfText } = kit
	const data = event.clipboardData
	if (data === null) return false
	const carried = pastedContent(data)
	if (carried.files.length > 0) {
		deps.attach(carried.files.map((file) => namePastedImage(file)))
		if (!carried.hasText) return true
	}
	const text = data.getData("text/plain")
	const html = data.getData("text/html")
	if (text === "" && html === "") return carried.files.length > 0

	const { state } = view
	if (deps.literal()) {
		view.dispatch(state.tr.replaceSelection(sliceOfText(text)).scrollIntoView())
		return true
	}
	switch (classifyClipboard({ html, text })) {
		case "own-slice":
			// ProseMirror pastes its own slice better than any parser here would.
			return false
		case "markdown": {
			const url = text.trim()
			if (!state.selection.empty && URL.test(url)) {
				const { from, to } = state.selection
				const link = M.link.create({ href: url })
				view.dispatch(state.tr.addMark(from, to, link).scrollIntoView())
				return true
			}
			view.dispatch(
				state.tr.replaceSelection(sliceOfDocument(dialect.parse(text))).scrollIntoView(),
			)
			return true
		}
		case "code": {
			const block = N.codeBlock.create(null, text === "" ? null : dialect.schema.text(text))
			view.dispatch(state.tr.replaceSelectionWith(block).scrollIntoView())
			return true
		}
		case "html":
			view.dispatch(
				state.tr
					.replaceSelection(sliceOfDocument(documentFromHtml(dialect, html)))
					.scrollIntoView(),
			)
			return true
	}
}
